// - fx/init.js
/**
 * FX — Minimal Scroll-Based Animation Engine
 * ------------------------------------------
 * FX maps the vertical position of an element inside the viewport
 * to a normalized progress value t ∈ [0, 1].
 *
 * Developers provide a callback(element, t, rect) to define animations.
 * FX does NOT impose any animation logic. You stay fully in control.
 *
 * CALLBACK FLEXIBILITY
 * --------------------
 * A callback can:
 *   - apply inline styles
 *   - interpolate CSS variables
 *   - combine multiple effects
 *   - use transforms, opacity, clip-path, filters, etc.
 *
 * Example:
 *   callback: (el, t, rect) => { el.style.opacity = t; }
 *
 *
 * USING HELPERS
 * -------------
 * FX includes optional helpers for common behaviors:
 *
 * 1) applyStyle(property, options)
 *    → Applies simple effects (opacity, transform, scale, blur…)
 *
 *    callback: applyStyle("opacity", { reverse: true })
 *
 *
 * 2) interpolateCSSVars({ map, reverse })
 *    → Ideal for clip-path, masks, gradients, or any effect that
 *      relies on CSS custom properties.
 *
 *    callback: interpolateCSSVars({
 *      reverse: true,
 *      map: {
 *        "--cx":   ["--start-x", "--end-x"],
 *        "--cy":   ["--start-y", "--end-y"],
 *        "--posY": ["--start-pos-y", "--end-pos-y"]
 *      }
 *    })
 *
 *
 * 3) combine(...callbacks)
 *    → Compose multiple effects into a single FX callback.
 *
 *    callback: combine(
 *      interpolateEllipse({ reverse: true }),
 *      applyStyle("opacity", { reverse: true })
 *    )
 *
 * combine() is ideal for mixing transform + opacity,
 * or combining multiple CSS variable interpolations.
 *
 *
 * LIMIT DEFINITIONS
 * -----------------
 * Limits define when:
 *   t = 0 (animation start)
 *   t = 1 (animation end)
 *
 * Format:
 *   {
 *     from: "top" | "bottom",
 *     value: "40vh" | "120px",
 *     offset?: number
 *   }
 *
 * Example:
 *   start: { from:"bottom", value:"60vh" }
 *   end:   { from:"bottom", value:"20vh" }
 *
 * → t = 0 when rect.top reaches 60vh from bottom
 * → t = 1 when rect.top reaches 20vh from bottom
 *
 *
 * VISUAL MODEL
 * ------------
 *   viewport
 *  ┌──────────────────────────────┐
 *  │       start line (40vh)      │
 *  │       end line   (60vh)      │
 *  └──────────────────────────────┘
 *
 * FX measures rect.top and maps it into t ∈ [0, 1].
 *
 *
 * SPECIAL CASE — FIXED HEADERS
 * -----------------------------
 * Negative space works naturally:
 *
 *   start: { from:"top", value:"0px" }
 *   end:   { from:"top", value:"-400px" }
 *
 * → t = 0 when rect.top = 0
 * → t = 1 when rect.top = -400
 *
 *
 * FULL EXAMPLE
 * ------------
 * new FX({
 *   selector: ".fadeUpEllipse",
 *   limits: {
 *     start: { from:"bottom", value:"60vh" },
 *     end:   { from:"bottom", value:"20vh" }
 *   },
 *   callback: combine(
 *     applyStyle("opacity", { reverse: true }),
 *     applyStyle("transform", {
 *       transformer: t => `translateY(${20 * (1 - t)}px)`
 *     }),
 *     interpolateEllipse({ reverse: true })
 *   )
 * });
 *
 * → opacity fades in
 * → translateY moves upward
 * → clip-path ellipse morphs at the same time
 *
 * CLEAN, MODULAR, AND FULLY CUSTOMIZABLE
 */

import { applyStyle } from "./effects/applyStyle";
import { interpolateEllipse } from "./effects/interpolateEllipse";
import { menuCircular } from "./effects/menuCircular";
import { FXBuilder } from "./FXBuilder";
import { FXClick } from "./FXClick";
import { FXIO } from "./FXIO";
import { FXImageSequence } from "./FXImageSequence";
import { debounceRAF } from "../utils/timing";
import { getStickyProgress } from "../scroll/utils/sticky-progress";
import { applyCSSVars } from "../utils/cssVars";

export function initFx() {

    // On non-home pages there is no loader — mark as exited immediately so
    // split-word and nav-bar animations are not blocked.
    if (!document.body.classList.contains('page-home')) {
        document.body.classList.add('loader-exited');
        document.body.classList.add('home-title-ready');
    }

    new FXIO({
        selector: "main > section, main > .--sections-wrapper, body > header, body > footer",
        waitForDocumentInit: true,
        add: "animate",
        imagesReadyClass: "images-ready",
        rootMargin: "0px 0px -30% 0px"
    });

    new FXIO({
        // The home hero (.video-frames) drives its own "draw-lines" state
        // from scroll progress (see onHeaderUpdate below) — letting this
        // generic on-enter observer touch it too made the lines flash in
        // immediately on load instead of waiting for the scroll threshold.
        selector: "main > section, main > .--sections-wrapper, body > header:not(.video-frames), body > footer",
        waitForDocumentInit: true,
        add: "draw-lines",
        rootMargin: "0px 0px -60% 0px"
    });

    new FXClick({

        selector: ".primary-button",
        class: "activeFx",
        delay: 1000,
        atEnd: "removeClass",

    });
    
    new FXBuilder()

        // - Opactiy entry
        .add({

            selector: "main > h3, main > h4, main > p, main > button, main > a, main .text-slide, main .image-slide",

            limits: {
                start: { axis: "y", from: "bottom", value: "20vh" },
                end:   { axis: "y", from: "bottom", value: "5vh" }
            },

            callback: applyStyle("opacity", { reverse: true })

        })

        // .add({
        //     selector: ".vertical-menu .item",

        //     // Quand les items doivent commencer/finir l’effet :
        //     limits: {
        //         start: { from:"top", value:"0vh" },
        //         end:   { from:"bottom", value:"100vh" } 
        //     },

        //     callback: menuCircular({
        //         container   : document.querySelector('.vertical-menu .item'),
        //         maxAngle    : 20,
        //         xCenter     : 220,   // position X la plus éloignée (au centre)
        //         xEdge       : 100,   // position X la plus proche (haut/bas)
        //         opacityMin  : 0.3,
        //     })
        // })

        .run();

    let needScrollDown = true;

    // Local state mirrors — avoids classList.contains() DOM reads on every animation frame.
    let stateInitLines      = false;
    let stateInitContainer  = false;
    let stateShowScrollDown = false;
    let stateForceNavbar    = false;

    let loaderExitRequested = false;
    let homeTitleReadyRequested = false;

    const parseTimeToMs = time => {
        const trimmed = time.trim();

        if (trimmed.endsWith("ms")) {
            return parseFloat(trimmed);
        }

        return parseFloat(trimmed) * 1000;
    };

    const getTransitionTotalMs = element => {
        if (!element) return 0;

        const styles = window.getComputedStyle(element);
        const durations = styles.transitionDuration.split(",").map(parseTimeToMs);
        const delays = styles.transitionDelay.split(",").map(parseTimeToMs);
        const maxLength = Math.max(durations.length, delays.length);

        let maxTotal = 0;

        for (let index = 0; index < maxLength; index++) {
            const duration = durations[index] ?? durations[durations.length - 1] ?? 0;
            const delay = delays[index] ?? delays[delays.length - 1] ?? 0;

            maxTotal = Math.max(maxTotal, duration + delay);
        }

        return maxTotal;
    };

    const unlockHomeLoader = () => {
        if (loaderExitRequested) return;

        loaderExitRequested = true;

        const loader = document.querySelector(".main-loader");
        const target = loader?.querySelector(".col.--left") || loader?.querySelector(".col");

        const finish = () => {
            document.body.classList.add("loader-exited");
        };

        if (!loader || !target) {
            document.body.classList.add("frames-ready");
            finish();
            return;
        }

        const onTransitionEnd = event => {
            if (event.target !== target || event.propertyName !== "transform") {
                return;
            }

            finish();
        };

        target.addEventListener("transitionend", onTransitionEnd, { once: true });

        document.body.classList.add("frames-ready");

        window.setTimeout(finish, getTransitionTotalMs(target) + 100);
    };

    const unlockHomeTitle = () => {
        if (homeTitleReadyRequested) return;

        homeTitleReadyRequested = true;

        window.setTimeout(() => {
            document.body.classList.add("home-title-ready");
        }, 900);
    };

    const onHeaderUpdate = (section, _media, progress) => {

        // Draw lines — structure the layout (content/button placement)
        // before the content itself fades in, not right at section entry.
        if (progress >= 0.5 && !stateInitLines) {
            stateInitLines = true;
            section.classList.add('draw-lines');
        } else if (progress < 0.5 && stateInitLines) {
            stateInitLines = false;
            section.classList.remove('draw-lines');
        }

        // Content
        if (progress >= 0.78 && !stateInitContainer) {

            stateInitContainer = true;
            section.classList.add('init-container');

            setTimeout(() => {
                section.classList.add('draw-lines', 'animated');
                stateInitLines = true;
            }, 200);

        } else if (progress < 0.78 && stateInitContainer) {

            stateInitContainer = false;
            stateInitLines     = false;
            section.classList.remove('init-container', 'draw-lines', 'animated');

        }

        if (needScrollDown) {

            if (!stateShowScrollDown && progress < 0.78) {
                stateShowScrollDown = true;
                document.body.classList.add('show-scroll-down');
            } else if (stateShowScrollDown && progress >= 0.78) {
                stateShowScrollDown = false;
                document.body.classList.remove('show-scroll-down');
                // needScrollDown = false;
            }

        }

        // Force nav bar
        if (!stateForceNavbar && progress >= 0 && progress < 1) {
            stateForceNavbar = true;
            document.body.classList.add('force-navbar');
        } else if (stateForceNavbar && progress >= 1) {
            stateForceNavbar = false;
            document.body.classList.remove('force-navbar');
        }

    };

    const loaderSpan  = document.querySelector('.main-loader .progress > span:first-child');
    const homeHeader = document.querySelector("body > header.--home");
    const frameCount = parseInt(homeHeader?.dataset.frameCount || "0", 10);
    const frameBase = homeHeader?.dataset.frameBase || "";
    const unlockAfter = Math.min(frameCount || 40, 40);
    const homeHeaderMinLoadDuration = 3000;

    const homeHeaderFramePath = index => {
        const number = String(index).padStart(3, "0");
        return `${frameBase}${number}.webp`;
    };

    // Touch devices get the lightweight hero consistently: this avoids
    // decoding/painting the full canvas frame sequence on mobile CPUs/GPUs
    // while keeping the desktop sequence intact.
    const isCoarsePointer = window.matchMedia("(pointer: coarse)").matches || window.matchMedia("(hover: none)").matches;
    const shouldUseLightweightHomeHeader = isCoarsePointer;

    const preloadPoster = src => {

        if (!src) {
            return Promise.resolve();
        }

        const img = new Image();
        img.decoding = "async";
        img.src = src;

        if (img.complete && (img.naturalWidth ?? img.width) > 0) {
            return Promise.resolve();
        }

        if (typeof img.decode === "function") {
            return img.decode().catch(() => new Promise(resolve => {
                img.addEventListener("load", resolve, { once: true });
                img.addEventListener("error", resolve, { once: true });
            }));
        }

        return new Promise(resolve => {
            img.addEventListener("load", resolve, { once: true });
            img.addEventListener("error", resolve, { once: true });
        });

    };

    const wait = duration => new Promise(resolve => {
        window.setTimeout(resolve, duration);
    });

    const animateLoaderProgress = duration => {

        if (!loaderSpan) {
            return wait(duration);
        }

        return new Promise(resolve => {

            const start = performance.now();

            const tick = now => {

                const progress = Math.min((now - start) / duration, 1);

                loaderSpan.textContent = Math.ceil(progress * 100);

                if (progress >= 1) {
                    resolve();
                    return;
                }

                requestAnimationFrame(tick);

            };

            loaderSpan.textContent = "0";
            requestAnimationFrame(tick);

        });

    };

    const activateStickyHomeHeader = () => {

        if (!homeHeader) {
            return;
        }

        document.body.classList.add("home-header-lightweight");

        applyCSSVars(homeHeader, {
            "--sequence-loaded": "1"
        });

        const syncHeaderProgress = debounceRAF(() => {

            const progress = getStickyProgress(homeHeader) / 100;

            applyCSSVars(homeHeader, {
                "--sequence-progress": progress.toFixed(4)
            });

            onHeaderUpdate(homeHeader, null, progress);

        });

        syncHeaderProgress();

        window.addEventListener("scroll", syncHeaderProgress, { passive: true });
        window.addEventListener("resize", syncHeaderProgress);

    };

    // Waits two frames so the compositor has actually painted the poster's
    // background-image before we consider it "ready" — decode() only
    // guarantees the bitmap is decoded, not that it has been painted yet.
    const waitForPaint = () => new Promise(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    const applyPosterImage = (posterEl, src) => {

        if (!posterEl || !src) {
            return Promise.resolve();
        }

        posterEl.style.backgroundImage = `url('${src}')`;

        return waitForPaint();

    };

    const initLightweightHomeHeader = () => {

        if (!homeHeader) {
            unlockHomeLoader();
            unlockHomeTitle();
            return;
        }

        // No CMS fallback image configured for this header — reuse the
        // sequence's own first frame as the static poster instead of
        // showing nothing.
        const posterSrc = homeHeader.dataset.fallbackImgSrc || homeHeaderFramePath(0);
        const posterEl = homeHeader.querySelector(".sequence-poster");

        // Decode fully off-screen first, only then apply it as the visible
        // background — this way the loader can't lift before the poster
        // is both decoded AND painted.
        const posterReady = preloadPoster(posterSrc)
            .then(() => applyPosterImage(posterEl, posterSrc));

        Promise.all([
            posterReady,
            animateLoaderProgress(homeHeaderMinLoadDuration)
        ]).finally(() => {
            activateStickyHomeHeader();
            unlockHomeLoader();
            unlockHomeTitle();
        });

    };

    if (document.body.classList.contains("page-home") && (!homeHeader || frameCount <= 0)) {
        unlockHomeLoader();
        unlockHomeTitle();
        return;
    }

    if (document.body.classList.contains("page-home") && shouldUseLightweightHomeHeader) {
        initLightweightHomeHeader();
        return;
    }

    new FXImageSequence({
        selector: "body > header.--home",
        canvas: ".scroll-sequence",
        frameCount,
        tau: 0,
        minDelta: 0.0003,
        concurrency: window.location.hostname === 'localhost' ? 6 : 16,
        framePath: homeHeaderFramePath,
        onUpdate:        onHeaderUpdate,
        cssVar:          "--sequence-progress",
        loadedVar:       "--sequence-loaded",
        readyAfter:      unlockAfter,
        minLoadDuration: homeHeaderMinLoadDuration,
        exitAt:          frameCount > 0 ? unlockAfter / frameCount : 0.1,
        onLoadProgress: (_section, _canvas, progress) => {
            if (loaderSpan) loaderSpan.textContent = Math.ceil(progress * 100);
        },
        onFirstFrameDraw: section => {
            section.classList.add("has-first-frame");
        },
        onLoadedWanted: () => {
            unlockHomeLoader();
            unlockHomeTitle();
        },
        onUnavailable: () => {
            unlockHomeLoader();
            unlockHomeTitle();
        }
    });

}
